import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GalleryInvitationDto, GalleryMemberAccess, GalleryMemberDto, GalleryTenantAccessLevel } from '@picflow/types';
import { lastValueFrom } from 'rxjs';
import { galleryMembersMaxAge } from './consts';
import { AppConfigService } from './providers/config.service';
import { DataLoaderService } from './services/data-loader.service';
import { GalleryPasswordService } from './services/gallery-password.service';
import { DisplayUserEmailDto } from './services/visitor.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryMemberService {
  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private loader: DataLoaderService,
    private galleryPasswordService: GalleryPasswordService
  ) {}

  public async getGalleryMembers(galleryId: string): Promise<GalleryMemberDto[]> {
    return this.loader.get(
      'gallery_members_' + galleryId,
      async () => {
        try {
          const resp = await lastValueFrom(
            this.http.get<{ data: GalleryMemberDto[] }>(
              `${
                this.config.apiEndpoint
              }/gallery_members?gallery=${galleryId}&expand[]=data.user&ignore_401_error=true&${this.getPassword(
                galleryId
              )}`
            )
          );
          return resp.data;
        } catch (err) {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return [];
        }
      },
      galleryMembersMaxAge
    );
  }

  public async getAllGalleryMembers(): Promise<GalleryMemberDto[]> {
    return this.loader.get(
      'gallery_members',
      async () => {
        try {
          const resp = await lastValueFrom(
            this.http.get<{ data: GalleryMemberDto[] }>(
              `${this.config.apiEndpoint}/gallery_members?expand[]=data.user&ignore_401_error=true`
            )
          );
          return this.makeMembersUnique(resp.data);
        } catch (err) {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return [];
        }
      },
      galleryMembersMaxAge
    );
  }

  public async getAllPendingGalleryMembers(): Promise<GalleryInvitationDto[]> {
    return this.loader.get(
      'gallery_invitations',
      async () => {
        try {
          const resp = await lastValueFrom(
            this.http.get<{ data: GalleryInvitationDto[] }>(
              `${this.config.apiEndpoint}/gallery_invitations?expand[]=data.user&ignore_401_error=true`
            )
          );
          return this.makeInvitesUnique(resp.data);
        } catch (err) {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return [];
        }
      },
      galleryMembersMaxAge
    );
  }

  public async getPendingGalleryMembers(galleryId: string): Promise<GalleryInvitationDto[]> {
    return this.loader.get(
      'gallery_invitations_' + galleryId,
      async () => {
        const resp = await lastValueFrom(
          this.http.get<{ data: GalleryInvitationDto[] }>(
            `${this.config.apiEndpoint}/gallery_invitations?gallery=${galleryId}`
          )
        );
        return resp.data;
      },
      galleryMembersMaxAge
    );
  }

  public async inviteMember(
    email: string,
    galleryId: string,
    accessLevel: GalleryTenantAccessLevel,
    notify: boolean,
    message: string
  ) {
    const params = {
      email,
      gallery: galleryId,
      access: accessLevel,
      message: message || undefined,
      notify: notify,
    };
    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/gallery_invitations`, params));
    this.loader.removeFuzzy('gallery_members');
    this.loader.removeFuzzy('gallery_invitations');
    return resp;
  }

  public async updateMember(memberId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    const resp = await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/gallery_members/${memberId}`, params));
    this.loader.removeFuzzy('gallery_members');
    return resp;
  }

  public async updateInvite(inviteId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    const resp = await lastValueFrom(
      this.http.patch(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`, params)
    );
    this.loader.removeFuzzy('gallery_invitations');
    return resp;
  }

  public async removeMember(memberId: string) {
    const resp = await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/gallery_members/${memberId}`));
    this.loader.removeFuzzy('gallery_members');
    return resp;
  }

  public async removeInvite(inviteId: string) {
    const resp = await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`));
    this.loader.removeFuzzy('gallery_invitations');
    return resp;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }

  makeMembersUnique(members: GalleryMemberDto[]) {
    const uniqueMembers: GalleryMemberDto[] = [];
    members.forEach(member => {
      const user = member.user as DisplayUserEmailDto;
      if (!uniqueMembers.find(m => m.user['id'] === user.id)) {
        uniqueMembers.push(member);
      }
    });
    return uniqueMembers;
  }

  makeInvitesUnique(invites: GalleryInvitationDto[]) {
    const uniqueInvites: GalleryInvitationDto[] = [];
    invites.forEach(invite => {
      if (!uniqueInvites.find(m => m.email === invite.email)) {
        uniqueInvites.push(invite);
      }
    });
    return uniqueInvites;
  }
}
